import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import SendBird from "sendbird";
import { sbGetRequestInboxChannels } from "../utils/sb";

type RequestChatState = {
  isLoading: boolean;
  isRefreshing: boolean;
  isFetching: boolean;
  error: string | null;
  channels: SendBird.GroupChannel[];
};

const requestInboxSlice = createSlice({
  name: "requestInbox",
  initialState: {
    isLoading: true,
    isRefreshing: false,
    isFetching: false,
    error: null,
    channels: [],
  } as RequestChatState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.isRefreshing = action.payload;
    },
    setFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setChannels: (
      state,
      {
        payload: { channels, reset = false },
      }: PayloadAction<{
        channels: SendBird.GroupChannel[];
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.channels = channels;
      } else {
        const mergedChannels = [...state.channels, ...channels];
        state.channels = mergedChannels;
      }
    },
    refresh: (state) => {
      state.isRefreshing = true;
      state.error = null;
    },
    setUnreadMessageCount: (
      state,
      {
        payload: { url, unreadMessageCount },
      }: PayloadAction<{ url: string; unreadMessageCount: number }>
    ) => {
      const updatedChannels = state.channels.map((channel) => {
        if (channel.url === url) {
          return { ...channel, unreadMessageCount };
        }

        return channel;
      });
      state.channels = updatedChannels;
    },
  },
});

export const {
  setLoading,
  setRefreshing,
  setFetching,
  setError,
  setChannels,
  refresh,
  setUnreadMessageCount,
} = requestInboxSlice.actions;

export const fetchRequestInboxChannels = () => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    dispatch(setChannels({ channels: [], reset: true }));
    const requestInboxChannels = await sbGetRequestInboxChannels();
    dispatch(setChannels({ channels: requestInboxChannels }));
  } catch (error) {
    dispatch(setError(error?.message));
  } finally {
    dispatch(setLoading(false));
  }
};

export const refreshRequestInboxChannels = () => async (dispatch) => {
  dispatch(setRefreshing(true));
  dispatch(setError(null));
  try {
    await dispatch(fetchRequestInboxChannels());
  } catch (error) {
    dispatch(setError(error?.message));
  } finally {
    dispatch(setRefreshing(false));
  }
};

export default requestInboxSlice.reducer;
